import React, { Suspense, useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LoadingScreen from './LoadingScreen';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  ExternalLink, 
  Scissors, 
  Store, 
  Heart,
  Car,
  ShoppingBag,
  Users,
  HelpCircle,
  MessageCircle,
  Leaf,
  Award,
  TreePine,
  GraduationCap
} from 'lucide-react';
import HeroSection from './HeroSection';
import { Canvas, useFrame } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  useAnimations,
  Environment,
  Center,
  Stars,
  PerspectiveCamera
} from "@react-three/drei";

// Préchargement du modèle - NOM CORRIGÉ
useGLTF.preload("/models/Untitled.glb");

// Modèle avec animations intégrées - VERSION DEBUG
function CubeModel() {
  const group = useRef();
  
  // Chargement avec logs de debug
  console.log('🔄 Tentative de chargement: /models/Untitled.glb');
  
  let gltfData;
  try {
    gltfData = useGLTF("/models/Untitled.glb");
    console.log('✅ GLB chargé:', gltfData);
    console.log('   Scene:', gltfData.scene);
    console.log('   Animations:', gltfData.animations);
  } catch (error) {
    console.error('❌ Erreur chargement GLB:', error);
    return null;
  }
  
  const { scene, animations } = gltfData;
  const { actions, mixer } = useAnimations(animations, group);
  
  useEffect(() => {
    console.log('\n🎬 Configuration animations...');
    console.log('   Scene existe:', !!scene);
    console.log('   Animations:', animations);
    console.log('   Actions:', actions);
    console.log('   Mixer:', mixer);
    
    // Jouer toutes les animations disponibles
    if (actions && Object.keys(actions).length > 0) {
      console.log('🎯 Animations disponibles:', Object.keys(actions));
      Object.entries(actions).forEach(([name, action]) => {
        if (action) {
          console.log(`   ▶️ Démarrage de "${name}"...`);
          action.reset().fadeIn(0.5).play();
          action.setLoop(2201, Infinity); // THREE.LoopRepeat = 2201
          console.log(`   ✅ "${name}" démarrée`);
        }
      });
    } else {
      console.warn('⚠️ Aucune animation trouvée !');
    }
    
    // Log de la scène
    console.log('\n📦 Contenu de la scène:');
    scene.traverse((child) => {
      console.log(`   - ${child.name || '(sans nom)'} (${child.type})`);
    });
    
    return () => {
      if (mixer) {
        mixer.stopAllAction();
      }
    };
  }, [actions, mixer, scene, animations]);

  // Animation de flottement + debug
  useFrame((state) => {
    // Debug toutes les 2 secondes
    const elapsed = state.clock.elapsedTime;
    if (Math.floor(elapsed) % 2 === 0 && elapsed % 1 < 0.016) {
      console.log('⏱️ useFrame actif:', elapsed.toFixed(1) + 's');
      if (group.current) {
        console.log('   Groupe existe: OUI');
        console.log('   Position Y:', group.current.position.y.toFixed(3));
      } else {
        console.log('   Groupe existe: NON');
      }
    }
    
    if (group.current) {
      const t = state.clock.getElapsedTime();
      group.current.position.y = Math.sin(t * 0.3) * 0.1;
    }
  });
  
  // Vérifier que scene existe avant le rendu
  if (!scene) {
    console.error('❌ Scene est null/undefined !');
    return null;
  }
  
  console.log('✅ Rendu du cube...');
  
  return (
    <group ref={group}>
      {/* Cube de test ROUGE - si tu le vois, Three.js fonctionne */}
      <mesh position={[-3, 0, 0]}>
        <boxGeometry args={[0.5, 0.5, 0.5]} />
        <meshStandardMaterial color="red" />
      </mesh>
      
      {/* Ton cube GLB - on augmente la taille pour être sûr de le voir */}
      <primitive
        object={scene}
        scale={[3.0, 3.0, 3.0]}  // TRÈS GROS pour test
        position={[0, 0, 0]}     // Centré
      />
      
      {/* Cube de test VERT - autre référence */}
      <mesh position={[3, 0, 0]}>
        <boxGeometry args={[0.5, 0.5, 0.5]} />
        <meshStandardMaterial color="green" />
      </mesh>
    </group>
  );
}

// Scène 3D avec le cube animé
function Scene3D() {
  return (
    <Canvas
      shadows
      className="fixed inset-0 -z-10"
      dpr={[1, 2]}
      gl={{ antialias: true, alpha: true }}
      onCreated={({ gl }) => {
        console.log('🎨 Canvas créé');
        console.log('   Renderer:', gl);
      }}
    >
      <color attach="background" args={["#0a0a1a"]} />
      <fog attach="fog" args={["#0a0a1a", 10, 25]} />
      
      {/* Éclairage optimisé */}
      <ambientLight intensity={0.6} />
      <directionalLight 
        position={[0, 5, 8]} 
        intensity={2} 
        castShadow 
        shadow-mapSize={[2048, 2048]}
      />
      <pointLight position={[5, 3, 5]} intensity={1.2} color="#ffffff" />
      <pointLight position={[-5, 3, 5]} intensity={1.2} color="#ffffff" />
      <pointLight position={[0, -3, 5]} intensity={0.8} color="#8888ff" />
      <pointLight position={[0, 0, -5]} intensity={0.5} color="#4ecdc4" />
      
      <Suspense fallback={
        <mesh>
          <boxGeometry args={[1, 1, 1]} />
          <meshStandardMaterial color="red" />
        </mesh>
      }>
        <PerspectiveCamera makeDefault position={[0, 1, 7]} fov={50} />
        
        <Center>
          <CubeModel />
        </Center>
        
        <Environment preset="city" />
        <OrbitControls 
          enableZoom={true}
          minDistance={5}
          maxDistance={15}
          enablePan={false}
          autoRotate={false}
          minPolarAngle={Math.PI / 6}
          maxPolarAngle={Math.PI - Math.PI / 6}
        />
        <Stars count={2000} depth={50} fade speed={0.5} factor={3} radius={100} />
      </Suspense>
    </Canvas>
  );
}

// Composant By avec animation contrôlée par JS
const AnimatedBy = () => {
  const [animated, setAnimated] = useState(false);
  const byRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimated(true);
      
      if (byRef.current) {
        byRef.current.style.transition = "all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)";
        byRef.current.style.opacity = "1";
        byRef.current.style.transform = "rotate(-10deg) translateY(0)";
      }
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <span 
      ref={byRef}
      style={{ 
        fontStyle: 'italic',
        display: 'inline-block', 
        margin: '0 5px',
        opacity: '0',
        transform: 'rotate(-45deg) translateY(-10px)',
        fontSize: '0.7em',
        color: '#ff9900',
        textShadow: '0 0 5px rgba(255, 153, 0, 0.7)'
      }}
    >
      By
    </span>
  );
};

const Home = () => {
  const [isLoading, setIsLoading] = useState(true);

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  const title = (
    <>
      Bienvenue à l'Annexe FACTORY
      <AnimatedBy />
      CFA Est Loiret - Villemandeur
    </>
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  const formations = [
    {
      icon: <Scissors className="w-8 h-8 text-purple-400" />,
      title: "Factory Beauté - Bien-être",
      description: "Formation aux métiers de la coiffure (CAP, BP, Mention) et de l'esthétique (CAP).",
      link: "/beaute-bien-etre",
      clickable: true
    },
    {
      icon: <Store className="w-8 h-8 text-blue-400" />,
      title: "Business Factory - Vente",
      description: "Formation aux métiers du commerce et de la vente (CAP EPC et Bac Pro MCV)",
      link: "/vente-gestion",
      clickable: true
    },
    {
      icon: <Heart className="w-8 h-8 text-pink-400" />,
      title: "Human Care Factory",
      description: "Formation aux métiers de l'éducation, de l'aide et du service (CAP AEPE et TP ADVF)",
      link: "/services-personne",
      clickable: true
    },
    {
      icon: <Car className="w-8 h-8 text-red-400" />,
      title: "Mobilité Factory",
      description: "Notre annexe spécialisée dans les formations du secteur automobile",
      link: "/automobile",
      clickable: true
    },
    {
      icon: <ShoppingBag className="w-8 h-8 text-indigo-400" />,
      title: "Business Factory  - Supérieur Vente & Gestion",
      description: "Formations avancées dans le domaine du commerce et de la vente",
      link: "/superieur-vente",
      clickable: true
    },
    {
      icon: <Users className="w-8 h-8 text-yellow-400" />,
      title: "Vie Étudiante",
      description: "Découvrez la vie au CFA, les activités et l'accompagnement proposés",
      link: "/vie-etudiante",
      clickable: true
    }
  ];

  return (
    <>
      <AnimatePresence>
        {isLoading && <LoadingScreen onLoadingComplete={handleLoadingComplete} />}
      </AnimatePresence>

      {/* Scène 3D en arrière-plan */}
      <Scene3D />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="relative min-h-screen pt-32 pb-20 px-4"
      >
        <div className="max-w-7xl mx-auto">
          {/* Section héro */}
          <div className="mb-20">
            <HeroSection 
              title={title} 
              subtitle="Centre de formation d'excellence proposant des formations en alternance dans divers secteurs professionnels" 
            />
          </div>

          {/* Section formations - 6 cartes */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-24"
          >
            {formations.map((formation, index) => {
              const CardContent = (
                <motion.div
                  variants={itemVariants}
                  whileHover={formation.clickable ? { 
                    scale: 1.02,
                    transition: { duration: 0.2 }
                  } : {}}
                  className={`bg-slate-800/60 p-8 rounded-lg backdrop-blur-sm border-l-4 border-blue-500 hover:border-pink-500 transition-all hover:shadow-lg hover:shadow-blue-500/20 h-full ${formation.clickable ? 'cursor-pointer' : ''}`}
                >
                  <div className="flex items-start space-x-4">
                    <motion.div 
                      className="p-3 bg-slate-700/50 rounded-lg"
                      whileHover={formation.clickable ? { rotate: 360 } : {}}
                      transition={{ duration: 0.5 }}
                    >
                      {formation.icon}
                    </motion.div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-3">{formation.title}</h3>
                      <p className="text-gray-300 text-sm leading-relaxed">{formation.description}</p>
                    </div>
                  </div>
                </motion.div>
              );

              return formation.clickable ? (
                <Link to={formation.link} key={index}>
                  {CardContent}
                </Link>
              ) : (
                <div key={index}>
                  {CardContent}
                </div>
              );
            })}
          </motion.div>

          {/* Section Green Factory */}
          <div className="max-w-4xl mx-auto mb-24">
            <Link to="/green-factory">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                whileHover={{ scale: 1.02 }}
                className="bg-green-700/30 backdrop-blur-md p-10 rounded-2xl cursor-pointer hover:shadow-2xl hover:shadow-green-500/20 transition-all border border-green-500/30"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <motion.div
                    animate={{ 
                      rotate: [0, 10, -10, 10, 0],
                      scale: [1, 1.1, 1, 1.1, 1]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      repeatDelay: 2
                    }}
                  >
                    <Leaf className="w-10 h-10 text-green-300" />
                  </motion.div>
                  <h2 className="text-3xl font-bold text-white">Green Factory</h2>
                </div>
                
                <h3 className="text-xl font-semibold text-green-200 mb-4">Notre nouveau pôle Écoconstruction</h3>
                
                <p className="text-green-50 leading-relaxed mb-6">
                  Le CFA Est Loiret lance son nouveau pôle Green Factory dédié aux métiers de l'écoconstruction et du bâtiment durable. Former les professionnels de demain pour bâtir durablement et répondre aux enjeux climatiques, c'est notre engagement. Parce que former, c'est déjà agir !
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-3 bg-green-600/20 p-3 rounded-lg border border-green-400/30">
                    <GraduationCap className="w-6 h-6 text-green-300" />
                    <span className="text-white font-semibold">CAP Écoconstruction</span>
                  </div>
                  <div className="flex items-center space-x-3 bg-green-600/20 p-3 rounded-lg border border-green-400/30">
                    <TreePine className="w-6 h-6 text-green-300" />
                    <span className="text-white font-semibold">Bâtiment durable</span>
                  </div>
                  <div className="flex items-center space-x-3 bg-green-600/20 p-3 rounded-lg border border-green-400/30">
                    <Award className="w-6 h-6 text-green-300" />
                    <span className="text-white font-semibold">Bachelor Énergie</span>
                  </div>
                </div>
              </motion.div>
            </Link>
          </div>

          {/* Section localisation - 3 adresses */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.5 }}
            className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-24"
          >
            {[
              { title: "Site Principal - Montargis", address: "3 rue Cyrille ROBERT, 45200 Montargis", color: "red" },
              { title: "Annexe - Villemandeur", address: "12 Rue des Frères Lumière, 45700 Villemandeur", color: "blue" },
              { title: "Green Factory - Montargis", address: "Maison Feuillette, 1 Place de la République, 45200 Montargis", color: "green" }
            ].map((location, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
                className="bg-slate-800/60 p-8 rounded-lg backdrop-blur-sm hover:shadow-lg hover:shadow-blue-500/20"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <motion.div
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    <MapPin className={`w-6 h-6 text-${location.color}-400`} />
                  </motion.div>
                  <h3 className="text-xl font-semibold text-white">{location.title}</h3>
                </div>
                <p className="text-gray-300 mb-2">{location.address}</p>
              </motion.div>
            ))}
          </motion.div>
          
          {/* Section Qualiopi */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.5 }}
            className="max-w-4xl mx-auto mb-24 text-center"
          >
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
              className="bg-slate-800/60 p-8 rounded-lg backdrop-blur-sm hover:shadow-lg hover:shadow-blue-500/20"
            >
              <h3 className="text-xl font-semibold text-white mb-4">Centre certifié Qualiopi</h3>
              <p className="text-gray-300 mb-6">
                Notre centre est certifié Qualiopi, garantissant la qualité des processus mis en œuvre dans nos actions de formation.
              </p>
              <div className="flex justify-center">
                <motion.img
                  src="/images/qualiopi-logo.png"
                  alt="Certification Qualiopi"
                  className="h-40 object-contain"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  whileHover={{ scale: 1.05 }}
                />
              </div>
            </motion.div>
          </motion.div>

          {/* Section boutons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-20"
          >
            <motion.a 
              href="https://www.cfa-montargis.com/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <span>Découvrir nos formations</span>
              <ExternalLink className="w-4 h-4" />
            </motion.a>
            
            <Link to="/contact">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 border border-white text-white hover:bg-white hover:text-slate-900 rounded-lg transition-colors flex items-center space-x-2"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Nous contacter</span>
              </motion.button>
            </Link>
            
            <Link to="/contact">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 border border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-slate-900 rounded-lg transition-colors flex items-center space-x-2"
              >
                <HelpCircle className="w-4 h-4" />
                <span>Accompagnement Handicap</span>
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </>
  );
};

export default Home;